import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import { buildReplyWithContextualMenuItems } from '../utils/attachButtonMenuItemBuilder';
import { aFPCreateAttachmentHandler } from '../utils/createAttachmentHandler';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { getMRUAttachmentStore } from 'owa-attachment-suggestions';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { IInlineImagePlugin } from 'owa-editor-plugins-list';
import clickAttachButton from 'owa-mail-compose-actions/lib/actions/clickAttachButton';
import trySaveMessage from 'owa-mail-compose-actions/lib/actions/trySaveMessage';
import { ComposeViewState } from 'owa-mail-compose-store';
import { ClientItem, mailStore } from 'owa-mail-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { logUserAction, SurfaceActionItem } from 'owa-surface-actions';
import * as React from 'react';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import {
    AttachmentSuggestions,
    ReplyWithAttachmentSuggestion,
    ReplyWithEndpointType,
} from 'owa-mail-store';
import {
    attachmentMultiMenuEntry,
    AttachmentMultiMenuEntry,
    AttachmentMenuInfo,
} from 'owa-attachment-multi-menu-actions';
import { IAttachmentLocalComputerFilePicker } from 'owa-attachment-filepicker';

export enum AttachButton {
    COMPUTER_ATTACHMENT_KEY = 'addMyComputerAttachment',
    ONLINE_LOCATION_KEY = 'addOnlineLocationAttachment',
    UPLOAD_AND_SHARE_FROM_CLOUD_KEY = 'addUploadAndShareFromCloud',
}

type AttachmentMultiMenuMap<T> = { [K in keyof AttachmentMultiMenuEntry]: T };

/**
 * To add a button, update the AttachButton enum, update getDefaultButtonsList, then update this method.
 */
export default function getAttachmentCommandItems(
    getAttachmentLocalComputerFilePicker: () => IAttachmentLocalComputerFilePicker | null,
    getAttachmentLocalComputerToCloudFilePicker: () => IAttachmentLocalComputerFilePicker | null,
    inlineImagePlugin: IInlineImagePlugin,
    viewState: ComposeViewState
): IContextualMenuItem[] {
    const openFilePicker = (
        filePickerToOpen: () => IAttachmentLocalComputerFilePicker | null,
        datapointName: string
    ) => {
        if (!viewState.itemId) {
            trySaveMessage(viewState);
        }
        const filePickerComp = filePickerToOpen();

        const datapoint = new PerformanceDatapoint(datapointName);
        datapoint.addCustomData([AttachmentMenuEntrySource.ToolBar]);

        if (filePickerComp) {
            filePickerComp.click();
            datapoint.end();
        } else {
            datapoint.endWithError(
                DatapointStatus.ClientError,
                new Error('Lazy component not loaded')
            );
        }
    };

    const attachmentMultiMenuInfo: AttachmentMultiMenuMap<{
        actionName: string;
        onClick: () => void;
    }> = {
        computer: {
            actionName: 'computerAttach',
            onClick: () =>
                openFilePicker(getAttachmentLocalComputerFilePicker, 'AttachLocalComputerClicked'),
        },
        onlineLocation: {
            actionName: 'onlineAttach',
            onClick: () => {
                clickAttachButton(
                    false /*isInline*/,
                    params => {
                        const baseProperties = { isInline: params.isInline, isHiddenInline: false };
                        const properties: CreateAttachmentsProperties = params.shouldShare
                            ? { ...baseProperties, shouldShare: true }
                            : { ...baseProperties, shouldShare: false };

                        aFPCreateAttachmentHandler(
                            params.files,
                            inlineImagePlugin,
                            viewState,
                            properties,
                            params.dataProviderType
                        );
                    },
                    viewState
                );
            },
        },
        uploadAndShareFromCloud: {
            actionName: 'computerToCloudAttach',
            onClick: () =>
                openFilePicker(
                    getAttachmentLocalComputerToCloudFilePicker,
                    'AttachUploadAndSharedClicked'
                ),
        },
    };
    const isSmimeCompose = isSmimeEnabledInViewState(viewState.smimeViewState);
    const defaultButtonTypes: AttachButton[] = isSmimeCompose
        ? getSmimeAttachButtonsList()
        : getDefaultButtonsList();

    const defaultButtons: IContextualMenuItem[] = defaultButtonTypes.map<IContextualMenuItem>(
        defaultButtonType => {
            switch (defaultButtonType) {
                case AttachButton.COMPUTER_ATTACHMENT_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.computer,
                        AttachButton.COMPUTER_ATTACHMENT_KEY,
                        attachmentMultiMenuInfo.computer.actionName,
                        attachmentMultiMenuInfo.computer.onClick
                    );
                case AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.uploadAndShareFromCloud,
                        AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.actionName,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.onClick
                    );
                case AttachButton.ONLINE_LOCATION_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.onlineLocation,
                        AttachButton.ONLINE_LOCATION_KEY,
                        attachmentMultiMenuInfo.onlineLocation.actionName,
                        attachmentMultiMenuInfo.onlineLocation.onClick
                    );
                default:
                    return assertNever(defaultButtonType);
            }
        }
    );

    // Attachment suggestions (replyWith + MRU)
    const referenceItem: ClientItem = mailStore.items.get(
        viewState.referenceItemId && viewState.referenceItemId.Id
    );
    const replyWithSuggestions: AttachmentSuggestions =
        referenceItem && referenceItem.AttachmentSuggestions;
    if (replyWithSuggestions && replyWithSuggestions.sentence) {
        replyWithSuggestions.sentence = replyWithSuggestions.sentence.filter(suggestion =>
            filterSuggestions(suggestion)
        );
    }
    const contextualAttachmentSuggestions =
        referenceItem && referenceItem.ContextualAttachmentSuggestions;
    const mruSuggestions: ReplyWithAttachmentSuggestion[] = getMRUAttachmentStore().attachments;

    if (
        !isSmimeCompose &&
        (!!replyWithSuggestions || !!mruSuggestions || !!contextualAttachmentSuggestions)
    ) {
        return buildReplyWithContextualMenuItems(
            viewState,
            replyWithSuggestions,
            [...(contextualAttachmentSuggestions || []), ...(mruSuggestions || [])].filter(
                suggestion => filterSuggestions(suggestion)
            )
        ).concat(defaultButtons);
    } else {
        return defaultButtons;
    }
}

export function filterSuggestions(suggestion: ReplyWithAttachmentSuggestion): boolean {
    if (!suggestion) {
        return false;
    }
    switch (suggestion.Endpoint) {
        case ReplyWithEndpointType.Contextual:
        case ReplyWithEndpointType.MRUCloudy:
        case ReplyWithEndpointType.MRUContextual:
            return referenceAttachmentsEnabled();
        case ReplyWithEndpointType.MRUClassic:
            return classicAttachmentsEnabled();
        default:
            return assertNever(suggestion.Endpoint);
    }
}

function createAttachmentContextMenuItem(
    menuInfo: AttachmentMenuInfo,
    attachButtonType: AttachButton,
    actionName: string,
    onClick: () => void
): IContextualMenuItem {
    const aria = menuInfo.ariaLabel && menuInfo.ariaLabel();

    return {
        key: attachButtonType,
        name: menuInfo.text(),
        ariaLabel: aria,
        title: aria,
        iconProps: menuInfo.iconProps,
        onClick: (
            event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
            item?: SurfaceActionItem
        ) => {
            logUserAction(true /* isCompose */, actionName, item);
            menuInfo.logUsage(AttachmentMenuEntrySource.ToolBar);
            onClick();
        },
    };
}

/**
 * Returns a list of button names (email/cloud/local etc) shown in the Attach button.
 * Used for logging. Needs to be consistent with getAttachmentCommandItems method.
 */
export function getDefaultButtonsList(): AttachButton[] {
    const defaultButtons: AttachButton[] = [];
    if (classicAttachmentsEnabled()) {
        defaultButtons.push(AttachButton.COMPUTER_ATTACHMENT_KEY);
    }
    if (classicAttachmentsEnabled() || referenceAttachmentsEnabled()) {
        defaultButtons.push(AttachButton.ONLINE_LOCATION_KEY);
    }

    if (attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled()) {
        defaultButtons.push(AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY);
    }

    return defaultButtons;
}

/**
 * Returns the list of buttons that needed to be shown in attach menu
 * in case of a S/MIME compose.
 */
export function getSmimeAttachButtonsList(): AttachButton[] {
    return [AttachButton.COMPUTER_ATTACHMENT_KEY];
}

function classicAttachmentsEnabled(): boolean {
    const userConfiguration = getUserConfiguration();
    return userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled;
}

function referenceAttachmentsEnabled(): boolean {
    const userConfiguration = getUserConfiguration();
    return userConfiguration.AttachmentPolicy.ReferenceAttachmentsEnabled;
}
